/**
 * The Command Center alert feed.
 *
 * The count in the sidebar pill and the rows in the panel are the same server
 * response — there is no second, client-derived count that could disagree with the
 * list the user opens. Alerts are actionable items (overdue reviews, waiting
 * approvals, celebrations), so they are refetched a little more eagerly than the
 * app-wide default.
 */
import { ListAlertsResponse } from "@hrms/shared";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type { z } from "zod";
import { api } from "@/lib/api";

export type AlertsPayload = z.infer<typeof ListAlertsResponse>;
export type Alert = AlertsPayload["alerts"][number];

export const alertKeys = {
  all: ["alerts"] as const,
  list: ["alerts", "list"] as const,
};

/** 60 s: fresh enough that a colleague's approval shows up, quiet enough to ignore. */
export const ALERTS_REFETCH_MS = 60_000;

export function useAlerts(): UseQueryResult<AlertsPayload> {
  return useQuery({
    queryKey: alertKeys.list,
    queryFn: () => api.get("/alerts", { schema: ListAlertsResponse }),
    refetchInterval: ALERTS_REFETCH_MS,
  });
}

/**
 * Removes a review alert from the reader's bell once they open it.
 *
 * The row and the badge drop immediately rather than after the round trip — the
 * reader has already navigated away by the time the server answers. A failed write
 * refetches, so the row comes back rather than being hidden by the client alone.
 */
export function useDismissAlert() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) => api.post("/alerts/dismiss", { id: alertId }),
    onMutate: async (alertId) => {
      await client.cancelQueries({ queryKey: alertKeys.list });
      client.setQueryData<AlertsPayload>(alertKeys.list, (current) => {
        if (!current) return current;
        const alerts = current.alerts.filter((alert) => alert.id !== alertId);
        return { alerts, count: alerts.length };
      });
    },
    onSettled: () => client.invalidateQueries({ queryKey: alertKeys.list }),
  });
}
